import { gql } from '@apollo/client';

export const GET_PRODUCTS = gql`
  query GetProducts {
    getProducts {
      id
      name
      tierId
      description
      createdAt
      pricing {
        isPurchasableQuery
        monthly {
          id
          amountCents
          currency
          isPurchasable
          trialPeriodDays
          coupons {
            id
            priceId
            stripeCouponId
            isActive
            pillDescription
            name: stripeName
            percentOff
            amountOff
            currency
            duration
            durationInMonths
            redeemBy
            createdAt
            updatedAt
          }
        }
        annual {
          id
          amountCents
          currency
          isPurchasable
          trialPeriodDays
          coupons {
            id
            priceId
            stripeCouponId
            isActive
            pillDescription
            name: stripeName
            percentOff
            amountOff
            currency
            duration
            durationInMonths
            redeemBy
            createdAt
            updatedAt
          }
        }
      }
    }
  }
`;

export const GET_PURCHASABLE_PRODUCTS = gql`
  query GetPurchasableProducts {
    getPurchasableProducts {
      id
      name
      tierId
      description
      createdAt
      pricing {        
        isPurchasableQuery
        monthly {
          id
          amountCents
          currency
          isPurchasable
          trialPeriodDays
          coupons {
            id
            priceId
            stripeCouponId
            isActive
            pillDescription
            name: stripeName
            percentOff
            amountOff
            currency
            duration
            durationInMonths
            redeemBy
            createdAt
            updatedAt
          }
        }
        annual {
          id
          amountCents
          currency
          isPurchasable
          trialPeriodDays
          coupons {
            id
            priceId
            stripeCouponId
            isActive
            pillDescription
            name: stripeName
            percentOff
            amountOff
            currency
            duration
            durationInMonths
            redeemBy
            createdAt
            updatedAt
          }
        }
      }
    }
  }
`;

export const SIGNUP_USER = gql`
  mutation SignupUser($input: SignupInput!) {
    signupUser(input: $input) {
      id
      email
      firstName
      lastName
      createdAt
      updatedAt
    }
  }
`;

export const CHANGE_PLAN = gql`
  mutation ChangePlan($input: PlanChangeInput!) {
    changePlan(input: $input) {
      success
      message
      immediate
      effectiveDate
      changeType
    }
  }
`;

export const PREVIEW_PLAN_CHANGE = gql`
  query PreviewPlanChange($userId: String!, $newProductId: String!, $newPriceId: String!, $couponId: String) {
    previewPlanChange(userId: $userId, newProductId: $newProductId, newPriceId: $newPriceId, couponId: $couponId) {
      success
      immediateCharge
      immediateCredit
      accountCredit
      nextInvoiceAmount
      nextInvoiceDate
      prorationDetails
      error
      billingStrategy
      accessTiming
      scheduledDate
      taxAmount
    }
  }
`;

export const PREVIEW_SIGNUP_INVOICE = gql`
  query PreviewSignupInvoice($priceId: String!, $couponId: String, $billingAddress: AddressInput!, $giftCodeApplied: Boolean, $marketingPromoCode: String) {
    previewSignupInvoice(priceId: $priceId, couponId: $couponId, billingAddress: $billingAddress, giftCodeApplied: $giftCodeApplied, marketingPromoCode: $marketingPromoCode) {
      success
      subtotal
      discountAmount
      discounts {
        type
        description
        amount
        duration
        durationInMonths
      }
      taxAmount
      total
      currency
      error
      errorCode
    }
  }
`;

export const CHECK_EMAIL_AVAILABILITY = gql`
  query CheckEmailAvailability($email: String!) {
    checkEmailAvailability(email: $email) {
      available
      message
    }
  }
`;

export const CHECK_USERNAME_AVAILABILITY = gql`
  query CheckUsernameAvailability($username: String!, $excludeUserId: String) {
    checkUsernameAvailability(username: $username, excludeUserId: $excludeUserId) {
      available
      message
    }
  }
`;

export const GET_USER_PROFILE = gql`
  query GetUserProfile($id: String!) {
    getUserProfile(id: $id) {
      id
      email
      firstName
      lastName
      userName
      shirtSize
      birthDate
      createdAt
      
      memberships {
        id
        status
        startedAt
        endedAt
        stripePriceId
        stripeSubscriptionId
        paymentProviderAccountId
        currentPeriodEnd
        billingCycleEndsOn
        createdAt
        updatedAt
        product {
          id
          name
          tierId
          description
          createdAt
          pricing {
            isPurchasableQuery
            monthly {
              id
              amountCents
              currency
              isPurchasable
            }
            annual {
              id
              amountCents
              currency
              isPurchasable
            }
          }
        }
        userRedemptions {
          id
          redemptionType
          redemptionCode
          redemptionSource
          valueType
          valueAmount
          metadata
          redeemedAt
          createdAt
          updatedAt
        }
      }
      
      membershipHistory {
        id
        status
        startedAt
        endedAt
        stripePriceId
        stripeSubscriptionId
        paymentProviderAccountId
        createdAt
        product {
          id
          name
          tierId
        }
        userRedemptions {
          id
          redemptionType
          redemptionCode
          redemptionSource
          valueType
          valueAmount
          metadata
          redeemedAt
          createdAt
          updatedAt
        }
      }
      
      addresses {
        id
        type
        street
        line2
        city
        state
        zip
        country
        createdAt
      }
    }
  }
`;

export const GET_USER_PROFILE_SUMMARY = gql`
  query GetUserProfileSummary($id: String!) {
    getUserProfileSummary(id: $id) {
      id
      email
      firstName
      lastName
      activeMembership {
        id
        status
        product {
          name
        }
        paymentProviderAccountId
      }
    }
  }
`;

export const GET_USER_ACCOUNT_BALANCE = gql`
  query GetUserAccountBalance($userId: String!) {
    getUserAccountBalance(userId: $userId)
  }
`;

export const GET_UPCOMING_INVOICE = gql`
  query GetUpcomingInvoice($userId: String!) {
    getUpcomingInvoice(userId: $userId) {
      amountDue
      subtotal
      total
      taxAmount
      currency
      periodStart
      periodEnd
      nextPaymentAttempt
      startingBalance
      appliedBalance
      discountTotal
      discounts {
        name
        amount
      }
      lineItems {
        description
        amount
        isProration
        periodStart
        periodEnd
      }
      planName
      planInterval
    }
  }
`;

export const CANCEL_SUBSCRIPTION = gql`
    mutation CancelSubscription(
        $userId: String!, 
        $reason: String,
        $surveyResponses: CancellationSurveyInput
    ) {
        cancelSubscription(
            userId: $userId, 
            reason: $reason,
            surveyResponses: $surveyResponses
        ) {
            success
            message
            accessEndsAt
            canReactivate
            subscriptionStatus
            daysUntilExpiration
        }
    }
`;

export const REACTIVATE_SUBSCRIPTION = gql`
    mutation ReactivateSubscription($userId: String!) {
        reactivateSubscription(userId: $userId) {
            success
            message
            subscriptionStatus
            nextBillingDate
        }
    }
`;

export const UPDATE_USER_ADDRESSES = gql`
    mutation UpdateUserAddresses(
        $userId: String!,
        $billingStreet: String!,
        $billingLine2: String,
        $billingCity: String!,
        $billingState: String!,
        $billingZip: String!,
        $billingCountry: String!,
        $shippingAddress: AddressInput
    ) {
        updateUserAddresses(
            userId: $userId,
            billingAddress: {
                street: $billingStreet,
                line2: $billingLine2,
                city: $billingCity,
                state: $billingState,
                zip: $billingZip,
                country: $billingCountry
            },
            shippingAddress: $shippingAddress
        ) {
            success
            message
        }
    }
`;

export const GET_PAYMENT_METHOD_INFO = gql`
    query GetPaymentMethodInfo($userId: String!) {
        getPaymentMethodInfo(userId: $userId) {
            last4
            brand
            expMonth
            expYear
        }
    }
`;

export const GET_MEMBERSHIP_CHANGE_LOGS = gql`
    query GetMembershipChangeLogs($userId: String!, $limit: Int, $offset: Int) {
        getMembershipChangeLogs(userId: $userId, limit: $limit, offset: $offset) {
            id
            userId
            userMembershipId
            fromProductId
            toProductId
            stripeSubscriptionId
            stripePriceId
            stripeEventId
            changeType
            changeAmount
            effectiveAt
            scheduledAt
            reason
            source
            metadata
            isConfirmed
            createdAt
        }
    }
`;

export const UPDATE_PAYMENT_METHOD = gql`
    mutation UpdatePaymentMethod($userId: String!, $paymentMethodId: String!) {
        updatePaymentMethod(userId: $userId, paymentMethodId: $paymentMethodId) {
            success
            message
            paymentMethodInfo {
                last4
                brand
                expMonth
                expYear
            }
        }
    }
`;

export const UPDATE_USER_PROFILE = gql`
    mutation UpdateUserProfile($userId: String!, $input: UserProfileUpdateInput!) {
        updateUserProfile(userId: $userId, input: $input) {
            success
            message
            user {
                id
                email
                firstName
                lastName
                shirtSize
                birthDate
                createdAt
            }
        }
    }
`;

export const GET_USER_PAYMENT_PROVIDERS = gql`
    query GetUserPaymentProviders($userId: String!) {
        getUserPaymentProviders(userId: $userId) {
            id
            provider
            providerCustomerId
            createdAt
        }
    }
`;

export const VALIDATE_GIFT_CODE = gql`
    query ValidateGiftCode($giftCodeId: String!, $userId: String) {
        validateGiftCode(giftCodeId: $giftCodeId, userId: $userId) {
            isValid
            error
            creditAmountCents
            productName
            length
            billingCycle
            currency
            calculationDescription
        }
    }
`;

export const REDEEM_GIFT_CODE = gql`
    mutation RedeemGiftCode($userId: String!, $input: RedeemGiftCodeInput!) {
        redeemGiftCode(userId: $userId, input: $input) {
            success
            message
            creditAmountCents
            customerBalanceCents
        }
    }
`;

export const CANCEL_SCHEDULED_PLAN_CHANGE = gql`
    mutation CancelScheduledPlanChange($userId: String!, $subscriptionId: String!) {
        cancelScheduledPlanChange(userId: $userId, subscriptionId: $subscriptionId) {
            success
            message
        }
    }
`;

export const DELETE_ACCOUNT = gql`
    mutation DeleteAccount {
        deleteAccount {
            success
            message
        }
    }
`;
